import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { previewStaffMessage, sendStaffMessage } from "../../Helpers/notificationConsoleApi";
import "../../Assets/css/notifications.css";
import "../../Assets/css/notification-centre.css";

const TITLE_MAX = 80;
const BODY_MAX = 300;
const MODES = [
    { key: "ids", label: "Picked people" },
    { key: "hub", label: "Everyone at a hub" },
    { key: "role", label: "Everyone with a role" },
    { key: "all", label: "All active staff" },
];

// A new id for each message being written, so a double click or a retry after a bad connection sends it once.
const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const hubLabel = (hub) => [hub.area, hub.city].filter(Boolean).join(", ") || hub.hubId;

// Write a message and send it to staff phones and staff inboxes. Nothing is sent until the review step is confirmed.
// Moved out of the Notification Centre to its own page next to Campaigns (R6.7).
const SendToStaffForm = () => {
    const [mode, setMode] = useState("ids");
    const [picked, setPicked] = useState([]);
    const [search, setSearch] = useState("");
    const [found, setFound] = useState([]);
    const [searching, setSearching] = useState(false);
    const [hubs, setHubs] = useState([]);
    const [roles, setRoles] = useState([]);
    const [hubId, setHubId] = useState("");
    const [roleId, setRoleId] = useState("");
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [urgent, setUrgent] = useState(false);
    const [review, setReview] = useState(null);
    const [confirmAll, setConfirmAll] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState("");
    const [done, setDone] = useState(null);
    const requestId = useRef(newRequestId());

    useEffect(() => {
        (async () => {
            const [hubResponse, roleResponse] = await Promise.all([
                Handler({ method: "GET", url: "/admin/hub/active", silent: true }),
                Handler({ method: "post", url: "/admin/staff/role/list/0", params: { count: 100 }, data: { status: true }, silent: true }),
            ]);
            setHubs(hubResponse?.success ? hubResponse?.data?.data || [] : []);
            setRoles(roleResponse?.success ? roleResponse?.data?.data?.list || [] : []);
        })();
    }, []);

    // Search active staff by name, id or phone, a moment after typing stops.
    useEffect(() => {
        if (mode !== "ids" || search.trim().length < 2) {
            setFound([]);
            return undefined;
        }
        setSearching(true);
        const timer = setTimeout(async () => {
            const response = await Handler({ method: "post", url: "/admin/staff/list/0", params: { count: 20 }, data: { search: search.trim(), employmentStatus: "active" }, silent: true });
            setFound(response?.success ? response?.data?.data?.list || [] : []);
            setSearching(false);
        }, 400);
        return () => { clearTimeout(timer); setSearching(false); };
    }, [search, mode]);

    const audience = useMemo(() => ({
        mode,
        ...(mode === "ids" ? { staffIds: picked.map((person) => person.staffId) } : {}),
        ...(mode === "hub" ? { hubId } : {}),
        ...(mode === "role" ? { roleId } : {}),
    }), [mode, picked, hubId, roleId]);

    // Any change to who or what is written cancels a review that was already open.
    useEffect(() => { setReview(null); setConfirmAll(false); setProblem(""); }, [audience, title, text, urgent]);

    const addPerson = (person) => {
        setPicked((list) => (list.some((item) => item.staffId === person.staffId) ? list : [...list, { staffId: person.staffId, name: person.name || person.staffId }]));
        setSearch("");
        setFound([]);
    };
    const removePerson = (staffId) => setPicked((list) => list.filter((item) => item.staffId !== staffId));

    const readyToReview = title.trim() && text.trim() && (
        (mode === "ids" && picked.length > 0) || (mode === "hub" && hubId) || (mode === "role" && roleId) || mode === "all"
    );

    const startReview = useCallback(async () => {
        setBusy(true);
        setProblem("");
        const result = await previewStaffMessage(audience);
        setBusy(false);
        if (!result.ok) { setReview(null); return setProblem(result.message); }
        return setReview(result.data);
    }, [audience]);

    const send = async () => {
        setBusy(true);
        setProblem("");
        const result = await sendStaffMessage({ ...audience, title: title.trim(), text: text.trim(), urgent, confirmAll, requestId: requestId.current });
        setBusy(false);
        if (!result.ok) return setProblem(result.message);
        toast.success(`Sent to ${result.data.recipients} ${result.data.recipients === 1 ? "person" : "people"}`);
        setDone({ ...result.data, title: title.trim(), withPhone: review?.withPhone, withoutPhone: review?.withoutPhone });
        setTitle("");
        setText("");
        setUrgent(false);
        setPicked([]);
        setReview(null);
        requestId.current = newRequestId();
        return null;
    };

    const who = review ? `${review.recipients} ${review.recipients === 1 ? "person" : "people"}` : "";

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-1">Send to staff</h5>
            <p className="text-muted small mb-3">Write a message and send it to staff phones and their in-app inbox. Only active staff are messaged. Tapping the message opens their inbox.</p>

            {done && (
                <div className="nc-sent" role="status">
                    <div className="fw-semibold">Sent: {done.title}</div>
                    <div className="nc-sub">
                        {done.recipients} {done.recipients === 1 ? "person" : "people"} got it in their inbox{done.urgent ? " (urgent)" : ""}.
                        {typeof done.withPhone === "number" ? ` ${done.withPhone} have a phone registered for push${done.withoutPhone ? `; ${done.withoutPhone} will only see it in their inbox` : ""}.` : ""}
                    </div>
                    <div className="nc-sub">See each delivery in the Delivery log.</div>
                </div>
            )}

            <div className="nc-sub mb-1">Who gets it</div>
            <div className="mb-3">
                {MODES.map((item) => (
                    <div className="form-check form-check-inline" key={item.key}>
                        <input className="form-check-input" type="radio" name="nc-staff-mode" id={`nc-mode-${item.key}`} checked={mode === item.key} onChange={() => setMode(item.key)} />
                        <label className="form-check-label small" htmlFor={`nc-mode-${item.key}`}>{item.label}</label>
                    </div>
                ))}
            </div>

            {mode === "ids" && (
                <div className="mb-3">
                    <input type="search" className="form-control form-control-sm" placeholder="Search staff by name, id or phone" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search staff" />
                    {searching && <div className="nc-sub mt-1">Searching...</div>}
                    {found.length > 0 && (
                        <div className="nc-staff-results">
                            {found.map((person) => (
                                <button key={person.staffId} type="button" className="nc-picker-item" onClick={() => addPerson(person)} disabled={picked.some((item) => item.staffId === person.staffId)}>
                                    <span className="nc-picker-name">{person.name || person.staffId}</span>
                                    <span className="nc-sub">{[person.staffId, person.roleName, person.hubId].filter(Boolean).join(" - ")}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {!searching && search.trim().length >= 2 && found.length === 0 && <div className="nc-sub mt-1">No active staff match.</div>}
                    <div className="nc-chips mt-2">
                        {picked.map((person) => (
                            <button key={person.staffId} type="button" className="nc-chip" onClick={() => removePerson(person.staffId)} title="Remove">{person.name} x</button>
                        ))}
                        {picked.length === 0 && <span className="nc-sub">Nobody picked yet.</span>}
                    </div>
                </div>
            )}

            {mode === "hub" && (
                <select className="form-select form-select-sm mb-3" value={hubId} onChange={(e) => setHubId(e.target.value)} aria-label="Hub">
                    <option value="">Choose a hub</option>
                    {hubs.map((hub) => <option key={hub.hubId} value={hub.hubId}>{hubLabel(hub)} ({hub.hubId})</option>)}
                </select>
            )}

            {mode === "role" && (
                <select className="form-select form-select-sm mb-3" value={roleId} onChange={(e) => setRoleId(e.target.value)} aria-label="Role">
                    <option value="">Choose a role</option>
                    {roles.map((role) => <option key={role.roleId} value={role.roleId}>{role.roleName}</option>)}
                </select>
            )}

            <label className="form-label small mb-1" htmlFor="nc-staff-title">Title <span className="nc-count">{title.length}/{TITLE_MAX}</span></label>
            <input id="nc-staff-title" className="form-control mb-3" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />

            <label className="form-label small mb-1" htmlFor="nc-staff-text">Message <span className="nc-count">{text.length}/{BODY_MAX}</span></label>
            <textarea id="nc-staff-text" className="form-control mb-2" rows={4} value={text} maxLength={BODY_MAX} onChange={(e) => setText(e.target.value)} />

            <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="nc-staff-urgent" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
                <label className="form-check-label small" htmlFor="nc-staff-urgent">Urgent: send even in quiet hours and to people who switched this kind of message off</label>
            </div>

            <div className="nc-preview-label">How it looks on the phone</div>
            <div className="nc-phone mb-3">
                <div className="nc-phone-title">{title.trim() ? `${urgent ? "Urgent: " : ""}${title.trim()}` : "Title"}</div>
                <div className="nc-phone-body">{text.trim() || "Your message"}</div>
            </div>

            {problem && <div className="nc-problem" role="alert">{problem}</div>}

            {!review ? (
                <div className="nc-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={startReview} disabled={busy || !readyToReview}>{busy ? "Checking..." : "Review and send"}</button>
                </div>
            ) : (
                <div className="nc-review" role="region" aria-label="Review before sending">
                    <div className="fw-semibold mb-1">This will go to {who}</div>
                    <div className="nc-sub mb-1">
                        {review.withPhone} {review.withPhone === 1 ? "has" : "have"} a phone registered for push{review.withoutPhone ? `; ${review.withoutPhone} will only see it in their inbox` : ""}.
                    </div>
                    {review.sample?.length > 0 && <div className="nc-sub mb-1">Including {review.sample.map((person) => person.name).join(", ")}{review.recipients > review.sample.length ? " and others" : ""}.</div>}
                    {review.skipped?.length > 0 && <div className="nc-sub mb-1">Not messaged (not active staff): {review.skipped.join(", ")}.</div>}
                    {urgent && <div className="nc-sub mb-1">Marked urgent: it ignores quiet hours and people's own switches.</div>}
                    {mode === "all" && (
                        <div className="form-check mb-2">
                            <input className="form-check-input" type="checkbox" id="nc-staff-all" checked={confirmAll} onChange={(e) => setConfirmAll(e.target.checked)} />
                            <label className="form-check-label small" htmlFor="nc-staff-all">I understand this goes to every active staff member</label>
                        </div>
                    )}
                    <div className="nc-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={send} disabled={busy || (mode === "all" && !confirmAll)}>{busy ? "Sending..." : `Send to ${who}`}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setReview(null)} disabled={busy}>Back</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const SendToStaff = () => (
    <div className="settings-page">
        <SendToStaffForm />
    </div>
);

export default SendToStaff;
